import { Dialog2, LoadingFullScreen, TinyMce, Checkbox } from 'components'
import { EmailMessageStatusBadge } from 'components/Badge'
import {
  getEmailThread, replyToEmailMessage, setEmailThreadComplete, getEmailReplyOptions,
  markEmailMessageUnread, assignEmailThread, downloadEmailAttachment
} from 'api/system'
import { getEmailTemplates } from 'api/emailTemplates'
import { getAdminUsers } from 'api/users'
import { isAdmin } from 'store/profile'
import { EMAIL_REPLY_TEMPLATE_TYPE } from 'pages/EmailTemplates/EditEmailTemplate'
import { DateTime } from 'luxon'

const INBOUND = 1
// The thread reads newest first, like a mail client. The newest messages stay open; anything older
// starts collapsed so a long thread stays readable.
const EXPANDED_MESSAGE_COUNT = 2
const MAX_IFRAME_HEIGHT = 600

const formatDate = iso => DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED)

const formatSize = bytes => {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const draftKey = id => `emailReplyDraft:${id}`

const readDraft = id => {
  try {
    return JSON.parse(window.localStorage.getItem(draftKey(id))) || {}
  } catch (e) {
    return {}
  }
}

const writeDraft = (id, draft) => {
  try {
    window.localStorage.setItem(draftKey(id), JSON.stringify(draft))
  } catch (e) {}
}

const clearDraft = id => {
  try {
    window.localStorage.removeItem(draftKey(id))
  } catch (e) {}
}

const readFileAsBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.substring(reader.result.indexOf(',') + 1))
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export const EmailMessageDialog = ({ attrs: { loading } }) => {
  loading = false

  let thread
  let replyOptions
  let loadedFor
  let editor
  let sending = false
  let errorMessage
  let snippets = []
  let adminUsers
  let reply

  getEmailTemplates({ pagesize: 500 }).then(data => {
    snippets = data.data.filter(template => template.templateType === EMAIL_REPLY_TEMPLATE_TYPE)
  })
  if (isAdmin()) {
    getAdminUsers({ pagesize: 100 }).then(data => {
      adminUsers = data.data
    })
  }

  const loadThread = (email) => {
    getEmailThread(email.id).then(data => {
      thread = data
      m.redraw()
    })
  }

  // `email` starts empty and is filled in by the list's getEmailMessage call, so nothing can be
  // fetched until an id has actually arrived.
  const load = (email) => {
    if (!email.id || loadedFor === email.id) {
      return
    }
    loadedFor = email.id
    if (email.emailThreadId) {
      loadThread(email)
    }
    if (canReply(email)) {
      const draft = readDraft(email.id)
      reply = {
        body: draft.body || '',
        cc: draft.cc || [],
        includeSignature: draft.includeSignature !== false,
        quoteOriginal: !!draft.quoteOriginal,
        attachments: []
      }
      getEmailReplyOptions(email.id).then(data => {
        replyOptions = data
        m.redraw()
      })
    }
  }

  // Without a thread a reply could not be matched back to anything, so only threaded mail and
  // received replies can be answered.
  const canReply = email => email.direction === INBOUND || !!email.emailThreadId

  const saveDraft = (email) => {
    writeDraft(email.id, {
      body: reply.body,
      cc: reply.cc,
      includeSignature: reply.includeSignature,
      quoteOriginal: reply.quoteOriginal
    })
  }

  const replyText = () => (editor ? editor.getContent({ format: 'text' }) : reply.body).trim()

  const hasUnsentReply = () => !!reply && (replyText().length > 0 || reply.attachments.length > 0)

  const attachmentBytes = () => reply.attachments.reduce((total, attachment) => total + attachment.size, 0)

  const addAttachments = (email, files) => {
    errorMessage = null
    Promise.all(Array.from(files).map(file => {
      return readFileAsBase64(file).then(contentBase64 => ({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
        contentBase64
      }))
    })).then(added => {
      reply.attachments = reply.attachments.concat(added)
      if (attachmentBytes() > replyOptions.maxAttachmentBytes) {
        errorMessage = `Attachments must total under ${formatSize(replyOptions.maxAttachmentBytes)}`
      }
      m.redraw()
    })
  }

  const sendReply = (email, onChanged) => {
    if (editor) {
      reply.body = editor.getContent()
    }
    if (!replyText()) {
      errorMessage = 'Write a reply before sending'
      return
    }
    if (attachmentBytes() > replyOptions.maxAttachmentBytes) {
      errorMessage = `Attachments must total under ${formatSize(replyOptions.maxAttachmentBytes)}`
      return
    }
    sending = true
    errorMessage = null
    replyToEmailMessage(email.id, {
      body: reply.body,
      isHtml: true,
      cc: reply.cc,
      includeSignature: reply.includeSignature,
      quoteOriginal: reply.quoteOriginal,
      attachments: reply.attachments.map(attachment => ({
        filename: attachment.filename,
        contentType: attachment.contentType,
        contentBase64: attachment.contentBase64
      }))
    })
      .then(() => {
        sending = false
        clearDraft(email.id)
        reply.body = ''
        reply.cc = []
        reply.attachments = []
        if (editor) {
          editor.setContent('')
        }
        // Re-read so the queued reply shows in the conversation straight away.
        if (email.emailThreadId) {
          loadThread(email)
        }
        onChanged && onChanged()
      })
      .catch(err => {
        sending = false
        errorMessage = (err && err.response) || 'Could not send the reply'
        m.redraw()
      })
  }

  const toggleComplete = (email, onChanged) => {
    const next = !(thread && thread.isComplete)
    setEmailThreadComplete(email.id, next).then(() => {
      loadThread(email)
      onChanged && onChanged()
    })
  }

  const assign = (email, userId, onChanged) => {
    assignEmailThread(email.id, userId || null).then(() => {
      loadThread(email)
      onChanged && onChanged()
    })
  }

  const toggleCc = (email, address) => {
    if (reply.cc.includes(address)) {
      reply.cc = reply.cc.filter(cc => cc !== address)
    } else {
      reply.cc = reply.cc.concat(address)
    }
    saveDraft(email)
  }

  return {
    view ({ attrs: { email, open, onChanged, onMarkedUnread } }) {
      load(email)

      // A received reply is stored with our own reply+token address in `to` and a Sent status, so it
      // has to be labelled by its sender instead — otherwise it reads as mail we sent ourselves.
      const isInbound = email.direction === INBOUND
      const messages = ((thread && thread.messages) || []).slice().reverse()
      const showComposer = canReply(email) && replyOptions

      return m(Dialog2, {
        open,
        heading: email.to && m('.flex.justify-between.pr-6', [
          isInbound ? `From: ${email.fromAddress}` : `To: ${email.to}`,
          m(EmailMessageStatusBadge, { status: isInbound ? 'Received' : email.statusName, statusId: email.emailMessageStatusId })
        ]),
        classes: ['md:max-w-3xl'],
        cancelLabel: 'Close',
        save: showComposer ? () => sendReply(email, onChanged) : null,
        saveLabel: 'Send reply',
        saving: sending,
        action: thread ? () => toggleComplete(email, onChanged) : null,
        actionLabel: thread && thread.isComplete ? 'Reopen' : 'Mark as complete',
        beforeClose () {
          if (sending || !hasUnsentReply()) {
            return true
          }
          const message = reply.attachments.length
            ? 'Your reply has not been sent and its attachments will be lost. Close anyway?'
            : 'Your reply has not been sent. Close anyway? The text is kept as a draft.'
          return window.confirm(message)
        }
      }, [
        email.to ? m('.flex.flex-col.gap-3', [
          thread && m(ThreadToolbar, {
            thread,
            adminUsers,
            isInbound,
            onAssign: userId => assign(email, userId, onChanged),
            onMarkUnread: () => {
              markEmailMessageUnread(email.id).then(() => {
                onMarkedUnread && onMarkedUnread()
                open(false)
              })
            }
          }),

          // With a thread we show the whole exchange so staff can read what was sent before
          // answering; without one there is only this message to show.
          messages.length
            ? m('.flex.flex-col.gap-3.max-h-[32rem].overflow-y-auto.pr-1', messages.map((msg, ix) => m(ThreadMessage, {
              key: msg.id,
              msg,
              expanded: ix < EXPANDED_MESSAGE_COUNT || msg.id === email.id
            })))
            : m('.flex.flex-col.gap-3', [
              m(MessageBody, { body: email.body }),
              isInbound
                ? email.receivedDate && m('p', `Received: ${formatDate(email.receivedDate)}`)
                : email.sentDate && m('p', `Sent: ${formatDate(email.sentDate)}`)
            ]),

          showComposer && m(ReplyComposer, {
            email,
            reply,
            replyOptions,
            snippets,
            errorMessage,
            getEditor: () => editor,
            onEditor: instance => {
              editor = instance
            },
            onBodyChange: body => {
              reply.body = body
              errorMessage = null
              saveDraft(email)
            },
            onToggleCc: address => toggleCc(email, address),
            onToggle: (field) => {
              reply[field] = !reply[field]
              saveDraft(email)
            },
            onAddFiles: files => addAttachments(email, files),
            onRemoveAttachment: attachment => {
              reply.attachments = reply.attachments.filter(a => a !== attachment)
              errorMessage = null
            }
          })
        ]) : m(LoadingFullScreen)
      ])
    }
  }
}

const ThreadToolbar = () => {
  return {
    view ({ attrs: { thread, adminUsers, isInbound, onAssign, onMarkUnread } }) {
      return m('.flex.flex-wrap.items-center.gap-3.text-sm', [
        adminUsers && m('label.flex.items-center.gap-2', [
          m('span.text-gray-600', 'Assigned to'),
          m('select.text-sm.rounded.border-gray-300.py-1', {
            value: thread.assignedToId || '',
            onchange: e => onAssign(e.target.value)
          }, [
            m('option', { value: '' }, 'Nobody'),
            adminUsers.map(user => m('option', { value: user.id }, user.fullName || user.userName))
          ])
        ]),
        !adminUsers && thread.assignedToName && m('span.text-gray-600', `Assigned to ${thread.assignedToName}`),
        isInbound && m('button.text-indigo-700.hover:text-indigo-900.underline', {
          type: 'button',
          onclick: onMarkUnread
        }, 'Mark as unread'),
        thread.isComplete && m('span.text-green-700.ml-auto', [
          'Completed',
          thread.completedByName ? ` by ${thread.completedByName}` : '',
          thread.completedDate ? ` on ${formatDate(thread.completedDate)}` : ''
        ])
      ])
    }
  }
}

// Inbound bodies are HTML written by whoever replied. Scripts never run in the frame; it is
// same-origin only so its height can be measured, which is safe while allow-scripts stays off.
const MessageBody = () => {
  const resize = dom => {
    const document = dom.contentDocument
    if (document && document.documentElement) {
      dom.style.height = `${Math.min(document.documentElement.scrollHeight + 8, MAX_IFRAME_HEIGHT)}px`
    }
  }
  return {
    view ({ attrs: { body } }) {
      return m('iframe.w-full.border-0', {
        srcdoc: body,
        sandbox: 'allow-same-origin',
        style: { height: '120px' },
        onload: e => resize(e.target)
      })
    }
  }
}

const ThreadMessage = ({ attrs: { expanded } }) => {
  let isExpanded = expanded
  return {
    view ({ attrs: { msg } }) {
      const isInbound = msg.direction === INBOUND
      const when = msg.receivedDate || msg.sentDate || msg.createdDate
      const attachments = msg.attachments || []
      return m('.border.rounded', {
        class: isInbound ? 'bg-gray-50' : 'bg-white'
      }, [
        m('.flex.justify-between.items-start.gap-2.px-2.py-1.text-xs.text-gray-600.border-b.cursor-pointer', {
          role: 'button',
          tabindex: 0,
          'aria-expanded': isExpanded ? 'true' : 'false',
          onclick: () => {
            isExpanded = !isExpanded
          },
          onkeydown: e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              isExpanded = !isExpanded
            }
          }
        }, [
          m('.flex.flex-col', [
            m('span.font-medium', isInbound ? `From: ${msg.fromAddress}` : `To: ${msg.to}`),
            isInbound && m('span', `To: ${msg.to}`),
            msg.cc && m('span', `Cc: ${msg.cc}`)
          ]),
          m('span.whitespace-nowrap', [
            attachments.length ? m('span.mr-2', `${attachments.length} attachment${attachments.length === 1 ? '' : 's'}`) : null,
            !isInbound && msg.statusName ? m('span.mr-2', msg.statusName) : null,
            when ? formatDate(when) : null
          ])
        ]),
        isExpanded && m(MessageBody, { body: msg.body }),
        isExpanded && attachments.length > 0 && m('.flex.flex-wrap.gap-2.px-2.py-2.border-t', attachments.map(attachment => {
          return m('button.text-xs.bg-gray-200.hover:bg-gray-300.rounded.px-2.py-1', {
            type: 'button',
            key: attachment.id,
            onclick: () => downloadEmailAttachment(attachment.emailMessageId, attachment)
          }, `${attachment.filename} (${formatSize(attachment.size)})`)
        }))
      ])
    }
  }
}

const ReplyComposer = () => {
  return {
    view ({ attrs: { email, reply, replyOptions, snippets, errorMessage, getEditor, onEditor, onBodyChange, onToggleCc, onToggle, onAddFiles, onRemoveAttachment } }) {
      return m('.border-t.pt-3.flex.flex-col.gap-2', [
        m('.text-sm.text-gray-700', [
          m('div', [m('span.font-medium', 'To: '), replyOptions.to]),
          m('div', [m('span.font-medium', 'Subject: '), replyOptions.subject])
        ]),

        replyOptions.ccOptions.length > 0 && m('div', [
          m('p.text-sm.font-medium.text-gray-700', 'Cc'),
          m('.grid.sm:grid-cols-2.gap-x-4', replyOptions.ccOptions.map(option => m(Checkbox, {
            key: option.email,
            value: option.email,
            checked: reply.cc.includes(option.email),
            label: `${option.name ? `${option.name} <${option.email}>` : option.email} (${option.type})`,
            onchange: () => onToggleCc(option.email)
          })))
        ]),

        snippets.length > 0 && m('label.flex.items-center.gap-2.text-sm', [
          m('span.text-gray-700', 'Insert snippet'),
          m('select.text-sm.rounded.border-gray-300.py-1', {
            value: '',
            onchange: e => {
              const snippet = snippets.find(s => String(s.id) === e.target.value)
              const editor = getEditor()
              if (snippet && editor) {
                editor.insertContent(snippet.body || '')
              }
              e.target.value = ''
            }
          }, [
            m('option', { value: '' }, 'Choose...'),
            snippets.map(snippet => m('option', { value: snippet.id }, snippet.description))
          ])
        ]),

        m(TinyMce, {
          name: `email_reply_${email.id}`,
          label: 'Reply',
          value: reply.body,
          height: 240,
          toolbar: 'undo redo | bold italic underline | bullist numlist | link | removeformat',
          onchange: onBodyChange,
          editor: instance => {
            onEditor(instance)
            instance.on('input keyup', () => onBodyChange(instance.getContent()))
          }
        }),

        m('.flex.flex-wrap.gap-x-6', [
          m(Checkbox, {
            value: 'includeSignature',
            checked: reply.includeSignature,
            label: 'Add my signature',
            onchange: () => onToggle('includeSignature')
          }),
          m(Checkbox, {
            value: 'quoteOriginal',
            checked: reply.quoteOriginal,
            label: 'Quote the message I am replying to',
            onchange: () => onToggle('quoteOriginal')
          })
        ]),

        m('.flex.flex-col.gap-1', [
          m('label.text-sm.text-gray-700', [
            m('span.font-medium', 'Attachments '),
            m('span.text-gray-500', `(up to ${formatSize(replyOptions.maxAttachmentBytes)} in total)`)
          ]),
          m('input.text-sm[type=file][multiple]', {
            onchange: e => {
              onAddFiles(e.target.files)
              e.target.value = ''
            }
          }),
          reply.attachments.length > 0 && m('.flex.flex-wrap.gap-2', reply.attachments.map(attachment => {
            return m('span.text-xs.bg-gray-200.rounded.px-2.py-1.flex.items-center.gap-1', [
              `${attachment.filename} (${formatSize(attachment.size)})`,
              m('button.text-red-700.hover:text-red-900.font-bold', {
                type: 'button',
                'aria-label': `Remove ${attachment.filename}`,
                onclick: () => onRemoveAttachment(attachment)
              }, '×')
            ])
          }))
        ]),

        errorMessage && m('p.text-sm.text-red-600', errorMessage),
        m('p.text-xs.text-gray-500', 'Your reply is queued and sent from ToBook, and stays on this record. Unsent text is kept as a draft on this computer.')
      ])
    }
  }
}
